// hooks/use-artifact-journey-data.js — ARTIFACT JOURNEY DATA LOADER
// ═══════════════════════════════════════════════════════════
// Owns loading + assembling everything the artifact journey map
// needs: artifacts, session metadata (for convergence detection),
// and version lineage. Returns a single { nodes, edges, sessions,
// loading, error } object ready to render.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { buildArtifactMap } from '../lib/artifact-map-layout';

export function useArtifactJourneyData({ userId, supabase, artifacts }) {
  const [sessionsById, setSessionsById] = useState({});
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!userId || !supabase || !Array.isArray(artifacts)) return;
    if (artifacts.length === 0) {
      setSessionsById({});
      setVersions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const sessionIds = Array.from(new Set(
      artifacts.map(a => a.session_id).filter(Boolean)
    ));
    const artifactIds = artifacts.map(a => a.id);

    Promise.all([
      sessionIds.length > 0
        ? supabase.from('sessions').select('id, session_type, title, created_at').in('id', sessionIds)
        : Promise.resolve({ data: [] }),
      artifactIds.length > 0
        ? supabase.from('artifact_versions').select('id, artifact_id, version_number, title, created_at').in('artifact_id', artifactIds)
        : Promise.resolve({ data: [] }),
    ]).then(([sessionsRes, versionsRes]) => {
      if (cancelled) return;
      const byId = {};
      (sessionsRes.data || []).forEach(s => { byId[s.id] = s; });
      setSessionsById(byId);
      setVersions(versionsRes.data || []);
    }).catch(e => {
      if (!cancelled) setError(e.message || 'Failed to load journey data');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => { cancelled = true; };
  }, [userId, supabase, artifacts]);

  const map = buildArtifactMap(artifacts || [], sessionsById, versions);
  return { ...map, loading, error };
}
